import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { useChat } from '@eneco/components/chat/useChat';
import { ChatEntryPoint as ChatEntryPointComponent } from '@eneco/custom-components/ChatEntryPoint';
import type { BadgeProps } from '@eneco/sparky/types';

import type { ChatEntryPointContent } from './chat-entry-point-content.types';

/**
 * `ChatEntryPoint` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/ChatEntryPoint/
 * ChatEntryPoint.tsx`): a clickable card opening the chat widget on a given topic. The card itself is
 * `@eneco/custom-components/ChatEntryPoint`, which the legacy stylesheet moved into verbatim — same
 * arrangement as `PromoBox`. This file supplies only the content and the chat wiring.
 *
 * `useChat` throws when mounted outside a chat provider — guarded the same way `ChatLink.tsx` (this
 * lane's other `useChat` consumer) already does, rendering nothing rather than crashing the page.
 */
const ChatEntryPoint: FC = () => {
  const content = useContent<ChatEntryPointContent>();
  const { chatTopic, messageText } = content.content ?? content;
  const { color, icon, text } = content.badge ?? content;

  try {
    const [mount] = useChat();
    const clickHandler = () => mount({ visibility: 'open', topic: chatTopic });

    return (
      <ChatEntryPointComponent
        onClick={clickHandler}
        badgeColor={color as BadgeProps['color']}
        badgeIcon={icon}
        badgeText={text}
        messageText={messageText}
      />
    );
    // Do not render anything if useChat throws an error, same as ChatLink.tsx.
  } catch {
    return null;
  }
};

export default ChatEntryPoint;
